import type { RenderStateCamera, RenderStateClippingPlane, SnapTolerance, View } from "@novorender/api";
import { computeRotation, rotationFromDirection } from "@novorender/api";
import type { HierarcicalObjectReference, SceneData, SceneLoadFail } from "@novorender/data-js-api";
import type { BoundingSphere, GeoLocation, Internal } from "@novorender/webgl-api";
import type { ReadonlyQuat, ReadonlyVec3 } from "gl-matrix";
import { mat3, quat, vec3, vec4 } from "gl-matrix";

import { dataApi } from "apis/dataV1";
// Type-only: `utils/objectData` imports `flip` from here, so a value import would cycle.
import type { ObjectDataAccess } from "dataAccess";
import { getCameraDir } from "features/engine2D/utils";
import type { Camera } from "features/viewpoints/types";
import { telemetry } from "telemetry";
import type { CustomProperties } from "types/project";

import type { RenderState } from "./renderSlice";
import type { CadCamera, CameraState, SceneConfig, Subtrees } from "./types";
import { SubtreeStatus } from "./types";

export const DEFAULT_CAMERA_PINHOLE_FAR = 10000;

export function getSubtrees(
    hidden: NonNullable<NonNullable<CustomProperties["explorerProjectState"]>["renderSettings"]>["hide"],
    subtrees: string[],
): Subtrees {
    return {
        terrain: subtrees.includes("terrain")
            ? hidden.terrain
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        lines: subtrees.includes("lines")
            ? hidden.lines
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        points: subtrees.includes("points")
            ? hidden.points
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        triangles: subtrees.includes("triangles")
            ? hidden.triangles
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        documents: subtrees.includes("documents")
            ? hidden.documents
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
    };
}

export function getLegacySubtrees(settings: Internal.RenderSettingsExt["advanced"], subtrees: string[]): Subtrees {
    return {
        terrain: subtrees.includes("terrain")
            ? settings.hideTerrain
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        lines: subtrees.includes("lines")
            ? settings.hideLines
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        points: subtrees.includes("points")
            ? settings.hidePoints
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        triangles: subtrees.includes("triangles")
            ? settings.hideTriangles
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
        documents: subtrees.includes("documents")
            ? settings.hideDocuments
                ? SubtreeStatus.Hidden
                : SubtreeStatus.Shown
            : SubtreeStatus.Unavailable,
    };
}
export function flip<T extends number[]>(v: T): T {
    const flipped = [...v];
    flipped[1] = -v[2];
    flipped[2] = v[1];
    return flipped as T;
}

export function flipGLtoCadQuat(b: quat) {
    const ax = 0.7071067811865475;
    const aw = 0.7071067811865475;
    const bx = b[0];
    const by = b[1];
    const bz = b[2];
    const bw = b[3];

    // prettier-ignore
    return quat.fromValues(
        ax * bw + aw * bx,
        aw * by + - ax * bz,
        aw * bz + ax * by,
        aw * bw - ax * bx);
}

export function flipCADToGLQuat(b: quat) {
    const ax = -0.7071067811865475,
        aw = 0.7071067811865475;
    const bx = b[0],
        by = b[1],
        bz = b[2],
        bw = b[3];

    // prettier-ignore
    return quat.fromValues(
        ax * bw + aw * bx,
        aw * by + - ax * bz,
        aw * bz + ax * by,
        aw * bw - ax * bx);
}

export function isGlSpace(up: Vec3 | undefined) {
    return !vec3.equals(up ?? [0, 1, 0], [0, 0, 1]);
}

export function latLon2Tm({ coords, tmZone }: { coords: GeoLocation; tmZone: string }) {
    return dataApi.latLon2tm(coords, tmZone);
}

export function tm2LatLon({ coords, tmZone }: { coords: ReadonlyVec3; tmZone: string }) {
    return dataApi.tm2LatLon(coords, tmZone);
}

export async function loadScene(id: string): Promise<[SceneConfig, CadCamera | undefined]> {
    return sceneConfigFromData(await dataApi.loadScene(id));
}

/**
 * The viewer's scene config, and its initial camera, from what `dataApi.loadScene` resolves with:
 * the scene, or the failure it is thrown as.
 */
export function sceneConfigFromData(
    res: (SceneData & { version?: string }) | SceneLoadFail,
): [SceneConfig, CadCamera | undefined] {
    let camera: CadCamera | undefined = undefined;

    if ("error" in res) {
        throw res;
    }

    const { ..._cfg } = res;
    const cfg = _cfg as SceneConfig;

    // Legacy scene config format
    // needs to be flipped.
    if (!cfg.customProperties?.initialCameraState) {
        if (cfg.camera && (cfg.camera.kind === "ortho" || cfg.camera.kind === "flight")) {
            camera =
                cfg.camera.kind === "ortho"
                    ? {
                          kind: "orthographic",
                          position: flip([
                              cfg.camera.referenceCoordSys[12],
                              cfg.camera.referenceCoordSys[13],
                              cfg.camera.referenceCoordSys[14],
                          ]),
                          rotation: rotationFromDirection(
                              flip([
                                  cfg.camera.referenceCoordSys[8],
                                  cfg.camera.referenceCoordSys[9],
                                  cfg.camera.referenceCoordSys[10],
                              ]),
                          ),
                          fov: cfg.camera.fieldOfView,
                      }
                    : {
                          kind: "pinhole",
                          position: flip(cfg.camera.position),
                          rotation: computeRotation(0, cfg.camera.pitch, cfg.camera.yaw),
                          fov: cfg.camera.fieldOfView,
                      };
        }
    } else {
        camera = cfg.customProperties.initialCameraState;
    }

    if (!cfg.customProperties.explorerProjectState && cfg.settings && cfg.settings.background) {
        cfg.settings.background.color = getBackgroundColor(cfg.settings.background.color);
    }

    // Some scene configs for some reason have it, while they're not supposed to
    if (
        cfg.customProperties.explorerProjectState?.renderSettings?.background &&
        "environments" in cfg.customProperties.explorerProjectState.renderSettings.background
    ) {
        delete cfg.customProperties.explorerProjectState.renderSettings.background.environments;
    }

    return [cfg, camera];
}

function getBackgroundColor(color: vec4 | undefined): vec4 {
    const grey: vec4 = [0.75, 0.75, 0.75, 1];
    const legacyBlue: vec4 = [0, 0, 0.25, 1];

    if (!color || vec4.exactEquals(color, legacyBlue)) {
        return grey;
    }

    return color;
}

export function applyCameraDistanceToMeasureTolerance(
    position: ReadonlyVec3,
    camera: RenderStateCamera,
    settings: SnapTolerance,
): SnapTolerance {
    let newObjectThreshold: number;
    if (camera.kind === "pinhole") {
        newObjectThreshold = vec3.dist(position, camera.position);
    } else {
        // In ortho distance between camera and object is irrelevant for mapping (as long as the object is visible),
        // so we calculate "perceived" distance from camera to object as if it was a pinhole camera.
        // Pinhole fov is 60 degrees, so camera, top and bottom of the screen make an equilateral triangle
        // with leg length equal to camera.fov and triangle height being distance from camera to object.
        // Simplifying Pythagoras theorem we get the formula below.
        newObjectThreshold = (Math.sqrt(3) / 2) * camera.fov;
    }
    const hoverScale = Math.min(Math.max(newObjectThreshold, 0.15), 100);

    return {
        edge: settings.edge ? settings.edge * hoverScale : undefined,
        face: settings.face ? settings.face * hoverScale : undefined,
        point: settings.point ? settings.point * hoverScale : undefined,
        segment: settings.segment ? settings.segment * hoverScale : undefined,
        edgeMidpoint: settings.edgeMidpoint ? settings.edgeMidpoint * hoverScale : undefined,
        arcCenter: settings.arcCenter ? settings.arcCenter * hoverScale : undefined,
    };
}

/**
 * @param dataAccess Awaited only by the sparse-scene fallback below, which is the one
 * branch here that needs a backend. A scene whose bounding box already places the camera
 * never awaits it, so a backend that is still resolving does not hold up first paint.
 */
export async function getDefaultCamera(
    dataAccess: Promise<ObjectDataAccess>,
    boundingBox?: { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number },
): Promise<CadCamera | undefined> {
    if (!boundingBox) {
        return;
    }

    const { minX, minY, maxX, maxY } = boundingBox;
    let { minZ, maxZ } = boundingBox;
    let centerX = (minX + maxX) / 2;
    let centerY = (minY + maxY) / 2;
    let centerZ = (minZ + maxZ) / 2;
    const width = maxX - minX;
    const height = maxY - minY;

    const fov = 60;
    const maxDim = Math.max(width, height);
    let distance = maxDim / (2 * Math.tan((fov * Math.PI) / 360));

    if (distance > DEFAULT_CAMERA_PINHOLE_FAR) {
        try {
            const sphere = await getSmallEnoughInitialObjectToLookAt(await dataAccess);
            if (sphere) {
                [centerX, centerY, centerZ] = sphere.center;
                minZ = Math.max(minZ, centerZ - sphere.radius);
                maxZ = Math.min(maxZ, centerZ + sphere.radius);
                distance = Math.min(sphere.radius, distance);
            }
        } catch (error) {
            console.error("Error getting small enough initial object to look at", error);
            telemetry.trackCaughtException({
                source: "render.renderUtils.getDefaultCamera",
                category: "render",
                error,
                severity: "error",
            });
        }
    }

    const initPosition = vec3.fromValues(0, 0, distance);
    const rotation = quat.fromValues(0.25000000000000006, 0.43301270189221935, 0.07945931129894554, 0.8623724356957945);
    const rotatedPosition = vec3.transformQuat(vec3.create(), initPosition, rotation);

    // Usually scenes are around z=0, so center it by default - centering at the ground level kinda makes sense.
    // If the model bbox is above/below z=0 - ground might no longer be applicable, so center at the middle of the bbox.
    // Not sure if we should center at the base of the bbox because it's conceptually closer to z=0 elevation,
    // but that won't hold for scenes with below-ground objects.
    // And also there's probably higher chance on average to have more objects in the view at the middle of the bbox.
    let zOffset = 0;
    if (minZ > 0 || maxZ < 0) {
        zOffset = centerZ;
    }

    const position = vec3.fromValues(
        rotatedPosition[0] + centerX,
        rotatedPosition[1] + centerY,
        rotatedPosition[2] + zOffset,
    );

    return {
        kind: "pinhole",
        position,
        rotation,
        fov,
    };
}

/**
 * Problem: user opens a very big scene where camera is not initialized yet.
 * This scene might be sparse, e.g. building standing kilometers away from each other.
 * Bounding volume in this case might have a void in the middle and the camera might be located too far (maybe even beyond max far plane distance).
 *
 * In this case we start going top-down the model tree searching for a small enough object to look at.
 *
 * Every backend puts bounds on the rows it hands back, so a row without them stands for an
 * object that has none - nothing to read, and the walk skips it.
 *
 * @returns the sphere the camera should look at on initial scene load
 */
function getSmallEnoughInitialObjectToLookAt(dataAccess: ObjectDataAccess): Promise<BoundingSphere | undefined> {
    const maxBoundingSphereRadius = DEFAULT_CAMERA_PINHOLE_FAR;

    // This depth should never be reached in practice unless they have crazy amount of folders or very deep hierarchy.
    const maxDepth = 10;

    type Candidate = { object: HierarcicalObjectReference; sphere: BoundingSphere };

    const doSearch = async (
        parent: HierarcicalObjectReference | undefined,
        depth: number,
    ): Promise<BoundingSphere | undefined> => {
        let largestSmallEnough: Candidate | undefined;
        let largest: Candidate | undefined;
        for await (const object of dataAccess.getChildren(parent)) {
            const sphere = object.bounds?.sphere;
            if (!sphere) {
                continue;
            }
            const radius = sphere.radius;
            if (
                radius < maxBoundingSphereRadius &&
                (!largestSmallEnough || radius > largestSmallEnough.sphere.radius)
            ) {
                largestSmallEnough = { object, sphere };
            }
            if (!largest || radius > largest.sphere.radius) {
                largest = { object, sphere };
            }
        }

        // We found a good enough object to look at.
        if (largestSmallEnough) {
            return largestSmallEnough.sphere;
        }

        // Take the largest object and continue drilling down.
        // The reason for taking the largest here is that we assume that the largest object has the most content,
        // so is the most interesting one (not always true, but let's assume so).
        // Choosing just one child also ensures we're not going to iterate over too many objects.
        if (largest && depth + 1 < maxDepth) {
            return await doSearch(largest.object, depth + 1);
        }

        return undefined;
    };

    return doSearch(undefined, 0);
}

export function getLocalRotationAroundNormal(quaternion: quat, normal: vec3): number {
    // Create a vector to represent the rotation axis
    const rotationAxis = vec3.create();
    quat.getAxisAngle(rotationAxis, quaternion);
    if (Math.abs(vec3.dot(rotationAxis, normal)) < 0.01) {
        return 0;
    }
    // Get the angle between the rotation axis and the normal
    const angle = vec3.angle(rotationAxis, normal);
    // Create a quaternion representing the rotation around the normal
    const rotationQuaternion = quat.setAxisAngle(quat.create(), normal, angle);
    // Decompose the object's quaternion into rotation around the normal and the remaining rotation
    const conjugateRotationQuaternion = quat.conjugate(quat.create(), rotationQuaternion);
    const localRotationQuaternion = quat.multiply(quat.create(), quaternion, conjugateRotationQuaternion);
    // Get the angle of the local rotation around the normal
    const localRotationAngle = 2 * Math.acos(localRotationQuaternion[3]);
    return localRotationAngle;
}

export function isTopDownRotation(rotation: ReadonlyQuat) {
    return vec3.dot(getCameraDir(rotation), vec3.fromValues(0, 0, -1)) >= 0.99;
}

export function nextFrame() {
    return new Promise<number>(requestAnimationFrame);
}

export function findCameraAlignedPlane(view: View) {
    if (!view.renderState.clipping.enabled || view.renderState.clipping.planes.length === 0) {
        return;
    }

    return findCameraAlignedPlaneInner(view.renderState.camera.rotation, view.renderState.clipping.planes);
}

export function findCameraAlignedPlaneInAppState(clipping: RenderState["clipping"], camera: CameraState) {
    if (!clipping.enabled || clipping.planes.length === 0 || !camera.goTo?.rotation) {
        return;
    }

    return findCameraAlignedPlaneInner(camera.goTo.rotation, clipping.planes);
}

function findCameraAlignedPlaneInner(rotation: ReadonlyQuat, planes: readonly RenderStateClippingPlane[]) {
    const cameraDir = getCameraDir(rotation);
    for (const plane of planes) {
        const planeNormal = vec3.fromValues(plane.normalOffset[0], plane.normalOffset[1], plane.normalOffset[2]);
        if (vec3.dot(planeNormal, cameraDir) < -0.999) {
            return plane;
        }
    }
}

/**
 * Converts a viewpoint `Camera` (direction + camera_up vectors) to a `CadCamera`
 * (position + rotation quaternion + fov) suitable for `renderActions.setInitialCamera`.
 */
export function viewpointCameraToCadCamera(camera: Camera): CadCamera {
    const getCameraRotation = (originalDirection: ReadonlyVec3, originalCameraUp: ReadonlyVec3): quat => {
        const direction = vec3.negate(vec3.create(), originalDirection);
        vec3.normalize(direction, direction);
        const cameraUp = vec3.normalize(vec3.create(), originalCameraUp);
        const cameraRight = vec3.cross(vec3.create(), cameraUp, direction);
        vec3.normalize(cameraRight, cameraRight);
        return quat.fromMat3(
            quat.create(),
            // prettier-ignore
            mat3.fromValues(
                cameraRight[0], cameraRight[1], cameraRight[2],
                cameraUp[0], cameraUp[1], cameraUp[2],
                direction[0], direction[1], direction[2],
            ),
        );
    };

    if ("perspective" in camera) {
        const { position, direction, camera_up: cameraUp, fov } = camera.perspective;
        return {
            kind: "pinhole",
            position: [...position] as vec3,
            rotation: getCameraRotation(direction, cameraUp),
            fov,
        };
    } else {
        const { position, direction, camera_up: cameraUp, vertical_extent } = camera.orthogonal;
        return {
            kind: "orthographic",
            position: [...position] as vec3,
            rotation: getCameraRotation(direction, cameraUp),
            fov: vertical_extent,
        };
    }
}

export function messageToWebkit(handlerName: string, message: unknown) {
    if ("webkit" in window && window.webkit) {
        const handler = (
            window as Window &
                typeof globalThis & {
                    webkit?: {
                        messageHandlers?: Record<
                            string,
                            {
                                postMessage: (msg: unknown) => void;
                            }
                        >;
                    };
                }
        ).webkit?.messageHandlers?.[handlerName];
        handler?.postMessage(message);
    }
}
